import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaLock, FaArrowLeft } from "react-icons/fa";

/* Shared design tokens + primitives for the IT Support module (submit page
   + admin approval queue) — same palette as the Checklist module so newly
   added tabs read as one visual family. */
export const T = {
  bg: "#f4f6f8",
  white: "#ffffff",
  border: "#e4e8ee",
  borderStrong: "#d7dce4",
  shadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
  shadowMd: "0 12px 32px -8px rgba(16,24,40,0.22)",
  radius: 14,
  ink1: "#101828",
  ink2: "#475467",
  ink3: "#98a2b3",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  primarySoft: "#eef2ff",
  amber: "#f59e0b",
  amberSoft: "#fffbeb",
  green: "#16a34a",
  greenSoft: "#f0fdf4",
  cyan: "#0891b2",
  cyanSoft: "#ecfeff",
  danger: "#e11d48",
  dangerSoft: "#fef2f2",
  fontDisplay: "'Space Grotesk', 'Segoe UI', ui-sans-serif, sans-serif",
  fontBody: "'Inter', 'Segoe UI', ui-sans-serif, sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, monospace",
};

export const CATEGORY_OPTIONS = [
  { value: "SOFTWARE_INSTALLATION", label: "Software Installation" },
  { value: "HARDWARE_ISSUE", label: "Hardware Issue" },
  { value: "ACCESS_REQUEST", label: "Access Request" },
  { value: "OTHER", label: "Other" },
];

export const PRIORITY_OPTIONS = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

export const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "REJECTED", label: "Rejected" },
];

export const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  ...STATUS_OPTIONS,
];

export function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label || value || "—";
}

export function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
      @keyframes itreq-spin { to { transform: rotate(360deg); } }
      @keyframes itreq-fade-in { from { opacity: 0; transform: translateY(6px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      .itreq-row:hover { background: #fafbfc; }
      .itreq-btn:focus-visible, .itreq-pill:focus-visible { outline: 2px solid ${T.primary}; outline-offset: 2px; }
      .itreq-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
      .itreq-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
      * { box-sizing: border-box; }
    `}</style>
  );
}

/* Click-outside pill dropdown — no native <select>, matches the rest of the app. */
export function PillSelect({ value, options, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="itreq-btn"
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`,
          background: T.white, color: T.ink1, fontSize: 13.5, fontFamily: T.fontBody, cursor: "pointer",
          transition: "border-color .12s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.borderStrong)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
      >
        <span>{selected ? selected.label : <span style={{ color: T.ink3 }}>{placeholder}</span>}</span>
        <FaChevronDown size={10} style={{ color: T.ink3, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 40,
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd,
          maxHeight: 220, overflowY: "auto", padding: 4, animation: "itreq-fade-in .12s ease",
        }}>
          {options.map((o) => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                padding: "8px 10px", borderRadius: 7, fontSize: 13.5, cursor: "pointer",
                color: o.value === value ? T.primary : T.ink1,
                background: o.value === value ? T.primarySoft : "transparent",
                fontWeight: o.value === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Spinner({ size = 30, padding = "60px 0" }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: `3px solid ${T.border}`, borderTopColor: T.primary,
        animation: "itreq-spin 0.7s linear infinite",
      }} />
    </div>
  );
}

const STATUS_STYLE = {
  PENDING:     { bg: T.amberSoft, fg: "#b45309" },
  APPROVED:    { bg: T.primarySoft, fg: T.primaryDark },
  IN_PROGRESS: { bg: T.cyanSoft, fg: T.cyan },
  RESOLVED:    { bg: T.greenSoft, fg: T.green },
  REJECTED:    { bg: T.dangerSoft, fg: T.danger },
};

export function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#f1f5f9", fg: T.ink2 };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5,
      fontWeight: 700, letterSpacing: ".2px", background: s.bg, color: s.fg, fontFamily: T.fontBody,
      whiteSpace: "nowrap",
    }}>
      {labelFor(STATUS_OPTIONS, status)}
    </span>
  );
}

/* Full-panel "you don't have this permission" state — used by the
   employee/manager IT Approval pages, which are only reachable if the
   "it_support_approval" permission was granted (Admin's own page needs no
   such check). Styled as a card rather than bare centered text so it reads
   as a deliberate state, not a broken page. */
export function AccessDenied({
  title = "Access Restricted",
  message = "You don't have permission to view this page.",
  backLabel = "Back to IT Support",
  onBack,
}) {
  return (
    <div style={{
      flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center",
      background: T.bg, fontFamily: T.fontBody, padding: 24,
    }}>
      <div style={{
        background: T.white, borderRadius: 18, boxShadow: T.shadowMd, border: `1px solid ${T.border}`,
        padding: "44px 40px", maxWidth: 380, width: "100%", textAlign: "center",
        animation: "itreq-fade-in .18s ease",
      }}>
        <div style={{
          width: 68, height: 68, borderRadius: "50%", background: T.dangerSoft,
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px",
          boxShadow: "0 0 0 6px rgba(225,29,72,0.06)",
        }}>
          <FaLock size={24} color={T.danger} />
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, color: T.ink1, fontFamily: T.fontDisplay, marginBottom: 8 }}>
          {title}
        </div>
        <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.6, marginBottom: onBack ? 26 : 0 }}>
          {message}
        </div>
        {onBack && (
          <button
            type="button"
            className="itreq-btn"
            onClick={onBack}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, margin: "0 auto",
              background: T.primary, color: "#fff", border: "none", borderRadius: 9,
              padding: "10px 22px", fontSize: 13.5, fontWeight: 600, fontFamily: T.fontBody,
              cursor: "pointer", boxShadow: "0 1px 2px rgba(99,102,241,0.3)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = T.primaryDark)}
            onMouseLeave={(e) => (e.currentTarget.style.background = T.primary)}
          >
            <FaArrowLeft size={11} /> {backLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000,
};
export const modalStyle = {
  width: "90%", background: T.white, borderRadius: T.radius, boxShadow: T.shadowMd, padding: 26,
  fontFamily: T.fontBody, animation: "itreq-fade-in .16s ease",
};
export const primaryBtnStyle = {
  background: T.primary, color: "#fff", border: "none", borderRadius: 9, padding: "10px 20px",
  fontSize: 13.5, fontWeight: 600, fontFamily: T.fontBody, cursor: "pointer",
};
export const textBtnStyle = {
  background: "transparent", border: "none", color: T.ink2, fontSize: 13.5, fontWeight: 600,
  cursor: "pointer", fontFamily: T.fontBody, padding: "10px 6px",
};
export const inputStyle = {
  width: "100%", border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px",
  fontSize: 13.5, color: T.ink1, fontFamily: T.fontBody, outline: "none",
};
